import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import NotifyCustomersLink from "../components/NotifyCustomersLink";
import { apiErrorMessage } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { formatDateTime } from "../services/datetime";
import { fetchNotices } from "../services/noticeService";
import { can } from "../services/permissions";

export const NOTICE_TYPES = [
  "staff_urgent_leave",
  "staff_running_late",
  "staff_left",
  "staff_moved_branch",
  "branch_temporary_closure",
  "opening_hours_change",
  "external_disruption",
  "price_change",
];

/** Disruption notices sent to customers (this reader's branches), newest first. */
export default function Notices() {
  const { t, i18n } = useTranslation();
  const maySend = can(getCurrentUser(), "notices.send");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ loading: true, rows: [], meta: null, error: "" });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: "" }));
    fetchNotices({ page, type })
      .then((body) => !cancelled && setState({ loading: false, rows: body.data, meta: body.meta, error: "" }))
      .catch(
        (err) =>
          !cancelled &&
          setState({
            loading: false,
            rows: [],
            meta: null,
            error: apiErrorMessage(err, t("notices.loadFailed")),
          })
      );
    return () => {
      cancelled = true;
    };
  }, [page, type, i18n.language, t]);

  const { loading, rows, meta, error } = state;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t("notices.title")}</h1>
          <p className="text-dense text-ink-2">{t("notices.subtitle")}</p>
        </div>
        {maySend && (
          <Link to="/notices/new" className="btn-primary h-9 px-4">
            {t("notices.notifyAffected")}
          </Link>
        )}
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <select
          className="field-input h-9 w-auto py-0"
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
          aria-label={t("notices.fields.type")}
        >
          <option value="">{t("notices.allTypes")}</option>
          {NOTICE_TYPES.map((key) => (
            <option key={key} value={key}>
              {t(`notices.types.${key}`)}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-raised" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-ink-2">
            <p>{t("notices.empty")}</p>
            {maySend && <NotifyCustomersLink className="mt-3" />}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-dense">
              <thead>
                <tr className="border-b border-line bg-raised text-caption text-ink-2">
                  <th className="px-4 py-2 text-start">{t("notices.fields.type")}</th>
                  <th className="px-4 py-2 text-start">{t("notices.fields.branches")}</th>
                  <th className="px-4 py-2 text-start">{t("notices.fields.sentAt")}</th>
                  <th className="px-4 py-2 text-end">{t("notices.counts.total")}</th>
                  <th className="px-4 py-2 text-end">{t("notices.counts.delivered")}</th>
                  <th className="px-4 py-2 text-end">{t("notices.counts.opened")}</th>
                  <th className="px-4 py-2 text-end">{t("notices.counts.rescheduled")}</th>
                  <th className="px-4 py-2 text-end">{t("notices.counts.cancelled")}</th>
                  <th className="px-4 py-2 text-end">{t("notices.counts.acknowledged")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((n) => (
                  <tr key={n.id} className="hover:bg-raised">
                    <td className="px-4 py-2">
                      <Link
                        to={`/notices/${n.id}`}
                        className="font-semibold text-accent-text hover:underline"
                      >
                        {n.type_label}
                      </Link>
                      <span className="block text-caption text-ink-2">
                        {n.created_by?.name ?? "—"} · {t(`notices.status.${n.status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-2">{n.branches.map((b) => b.name).join(", ")}</td>
                    <td className="px-4 py-2">{formatDateTime(n.sent_at ?? n.created_at, i18n.language)}</td>
                    <td className="px-4 py-2 text-end">{n.counts.total}</td>
                    <td className="px-4 py-2 text-end">{n.counts.delivered}</td>
                    <td className="px-4 py-2 text-end">{n.counts.opened}</td>
                    <td className="px-4 py-2 text-end">{n.counts.rescheduled}</td>
                    <td className="px-4 py-2 text-end">{n.counts.cancelled}</td>
                    <td className="px-4 py-2 text-end">{n.counts.acknowledged}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta && meta.last_page > 1 && (
          <div className="flex items-center justify-between border-t border-line px-4 py-3 text-dense text-ink-2">
            <span>{t("notices.total", { count: meta.total })}</span>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secondary h-8 px-3"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                {t("notices.previous")}
              </button>
              <button
                type="button"
                className="btn-secondary h-8 px-3"
                disabled={page >= meta.last_page}
                onClick={() => setPage(page + 1)}
              >
                {t("notices.next")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
